<template>
  <Teleport to="body">
    <div class="dialog-overlay" @click.self="close">
      <div class="dialog-panel" role="dialog" aria-modal="true" :aria-label="$t('cardPreview.title')">
        <div class="dialog-header">
          <h2 class="dialog-title">{{ $t('cardPreview.title') }}</h2>
          <div class="lang-switcher" role="tablist" :aria-label="$t('cardPreview.title')">
            <button
              v-for="loc in LOCALES"
              :key="loc"
              type="button"
              class="lang-btn"
              :class="{ 'is-active': previewLang === loc }"
              role="tab"
              :aria-selected="previewLang === loc"
              @click="previewLang = loc"
            >
              {{ loc.toUpperCase() }}
            </button>
          </div>
          <button
            ref="closeBtn"
            type="button"
            class="dialog-close"
            :aria-label="$t('cardPreview.close')"
            @click="close"
          >×</button>
        </div>

        <div class="dialog-body">
          <div class="preview-card">
            <div class="preview-type">{{ form.cardType }}</div>
            <h3 class="preview-title" :class="{ 'is-placeholder': !hasTitle }">
              {{ titleText }}
            </h3>

            <!-- Part 1: BODY_1 + IMAGE_1 — renders only if at least one is present. -->
            <template v-if="hasPart1">
              <p v-if="bodyText('body1')" class="preview-body">{{ bodyText('body1') }}</p>
              <div v-if="image1Record" class="preview-image-wrap">
                <img
                  class="preview-image"
                  :src="imageUrl(image1Record)"
                  :alt="altTextFor(image1Record, previewLang)"
                  @error="onImgError($event, image1Record, previewLang)"
                />
              </div>
            </template>

            <!-- Part 2: BODY_2 + IMAGE_2 — renders only if at least one is present. -->
            <template v-if="hasPart2">
              <p v-if="bodyText('body2')" class="preview-body">{{ bodyText('body2') }}</p>
              <div v-if="image2Record" class="preview-image-wrap">
                <img
                  class="preview-image"
                  :src="imageUrl(image2Record)"
                  :alt="altTextFor(image2Record, previewLang)"
                  @error="onImgError($event, image2Record, previewLang)"
                />
              </div>
            </template>

            <!-- Part 3: BODY_3 only (no IMAGE_3 in the schema). -->
            <p v-if="bodyText('body3')" class="preview-body">{{ bodyText('body3') }}</p>
          </div>
        </div>

        <div class="dialog-footer">
          <button type="button" class="btn btn-primary" @click="close">
            {{ $t('cardPreview.close') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { imageUrl, altTextFor, onImgError } from '@/services/imageHelpers';

const LOCALES = ['en', 'de', 'fr', 'it'];

// Map preview-card "slot" names to backend block kinds.
const KIND_BY_SLOT = {
  title: 'TITLE',
  body1: 'BODY_1',
  body2: 'BODY_2',
  body3: 'BODY_3',
};

export default {
  name: 'CardPreview',
  props: {
    // Current form state from CardEditor, so unsaved edits show in every
    // language. Text lives at form.text[KIND][locale].
    form: { type: Object, required: true },
    // Language to open on: the tab the author was editing.
    initialLang: { type: String, default: 'en' },
    // Map<id, ImageRecord> shared from the parent for image lookups.
    imageCache: { type: Map, required: true },
  },
  emits: ['close'],
  data() {
    return {
      previewLang: LOCALES.includes(this.initialLang) ? this.initialLang : 'en',
      previouslyFocusedElement: null,
      LOCALES,
    };
  },
  computed: {
    image1Record() {
      return this.form.image1Id != null
        ? this.imageCache.get(this.form.image1Id) || null
        : null;
    },
    image2Record() {
      return this.form.image2Id != null
        ? this.imageCache.get(this.form.image2Id) || null
        : null;
    },
    hasTitle() {
      return !!this.textFor('title');
    },
    // A part renders only if it has body text in the active language OR an
    // image. Empty parts disappear entirely — no spacer, no placeholder. The
    // vertical rhythm comes from the parts that exist.
    hasPart1() {
      return !!this.textFor('body1') || !!this.image1Record;
    },
    hasPart2() {
      return !!this.textFor('body2') || !!this.image2Record;
    },
    titleText() {
      const t = this.textFor('title');
      if (t) return t;
      return this.$t('cardPreview.noTitle', { lang: this.previewLang.toUpperCase() });
    },
  },
  mounted() {
    this.previouslyFocusedElement = document.activeElement;
    document.addEventListener('keydown', this.onKeydown);
    this.$nextTick(() => {
      if (this.$refs.closeBtn) this.$refs.closeBtn.focus();
    });
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    const el = this.previouslyFocusedElement;
    if (el && typeof el.focus === 'function') el.focus();
  },
  methods: {
    imageUrl,
    altTextFor,
    onImgError,
    onKeydown(e) {
      if (e.key === 'Escape') this.close();
    },
    close() {
      this.$emit('close');
    },
    // Strictly the previewed language, with no fallback, so the preview
    // shows exactly which translations are still missing. (Participants
    // do get a fallback: see resolveText in the runtime ContentCard.)
    textFor(slot) {
      const v = this.form.text?.[KIND_BY_SLOT[slot]]?.[this.previewLang];
      return typeof v === 'string' ? v.trim() : '';
    },
    bodyText(slot) {
      return this.textFor(slot);
    },
  },
};
</script>

<style scoped>
/* --- Dialog frame (mirrors the Image Picker; kept scoped per the no-promotion preference) --- */

.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
}

.dialog-panel {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */

.dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid #e9ecef;
}

.dialog-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  flex: 0 0 auto;
}

.dialog-close {
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
  border-radius: 4px;
  flex: 0 0 auto;
  margin-left: auto;
  transition: color 0.15s ease, background-color 0.15s ease;
}
.dialog-close:hover { color: #2c3e50; background: #f1f3f5; }
.dialog-close:focus-visible { outline: 2px solid #42b983; outline-offset: 2px; }

/* Language switcher */

.lang-switcher {
  display: flex;
  gap: 4px;
  flex: 1 1 auto;
  justify-content: center;
}

.lang-btn {
  padding: 4px 10px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  background: transparent;
  border: 1px solid #ced4da;
  border-radius: 6px;
  color: #6c757d;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  min-width: 36px;
}
.lang-btn:hover {
  color: #2c3e50;
  border-color: #adb5bd;
}
.lang-btn.is-active {
  background: #42b983;
  border-color: #42b983;
  color: #fff;
}
.lang-btn:focus-visible {
  outline: 2px solid #42b983;
  outline-offset: 2px;
}

/* Body */

.dialog-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 24px 28px;
  background: #fff;
}

/* --- Preview card --- */

.preview-card {
  max-width: 480px;
  margin: 0 auto;
}

.preview-type {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #42b983;
  margin-bottom: 6px;
}

.preview-title {
  font-size: 22px;
  font-weight: 500;
  line-height: 1.3;
  color: #2c3e50;
  margin: 0 0 1rem 0;
}

.preview-title.is-placeholder {
  color: #adb5bd;
  font-style: italic;
  font-weight: 400;
}

.preview-body {
  font-size: 15px;
  line-height: 1.6;
  color: #2c3e50;
  margin: 0 0 1.25rem 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.preview-image-wrap {
  margin: 0 0 1.25rem 0;
}

.preview-image {
  display: block;
  width: 100%;
  max-height: 320px;
  object-fit: contain;
  border-radius: 8px;
  background: #f8f9fa;
}

/* Footer */

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e9ecef;
  background: #fff;
}

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 9px 20px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease;
  letter-spacing: 0.3px;
}

.btn-primary { background: #42b983; color: #fff; }
.btn-primary:hover { background: #36a273; }
.btn-primary:focus-visible { outline: 2px solid #2E7D32; outline-offset: 2px; }

/* Responsive */

@media (max-width: 600px) {
  .dialog-overlay { padding: 8px; }
  .dialog-panel { max-height: 95vh; }
  .dialog-header { flex-wrap: wrap; }
  .lang-switcher {
    order: 3;
    width: 100%;
    justify-content: flex-start;
    margin-top: 4px;
  }
  .dialog-body { padding: 18px 20px; }
}
</style>
